import * as React from 'react';
import { cn } from '../utils/cn';

// Az `Omit<…, 'title'>` azért kell, mert a natív HTML `title` prop string,
// de a Hero-ban tetszőleges ReactNode (akár ikonnal együtt) kerülhet bele.
export interface HeroProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  illustration?: React.ReactNode;
}

/**
 * Lila gradiens hero — a marketplace landing page-ek tipikus mintája.
 * Bal oldalt szöveg + CTA, jobb oldalt opcionális illusztráció.
 */
export function Hero({
  eyebrow,
  title,
  subtitle,
  actions,
  illustration,
  className,
  ...props
}: HeroProps) {
  return (
    <section
      className={cn(
        'bg-brand-gradient relative overflow-hidden text-white',
        'rounded-3xl',
        className,
      )}
      {...props}
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_75%_15%,rgba(255,255,255,0.18),transparent_55%)]" />
      <div className="relative mx-auto flex max-w-7xl flex-col items-center gap-10 px-6 py-16 sm:px-8 sm:py-20 lg:flex-row lg:gap-16 lg:py-24">
        <div className="flex-1 text-center lg:text-left">
          {eyebrow ? (
            <p className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider backdrop-blur-md">
              {eyebrow}
            </p>
          ) : null}
          <h1 className="font-display text-4xl font-bold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
            {title}
          </h1>
          {subtitle ? (
            <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-white/85 sm:text-lg lg:mx-0">
              {subtitle}
            </p>
          ) : null}
          {actions ? (
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3 lg:justify-start">
              {actions}
            </div>
          ) : null}
        </div>
        {illustration ? <div className="flex-1">{illustration}</div> : null}
      </div>
    </section>
  );
}
